import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Trash2, Copy, GripVertical } from 'lucide-react';
import { Button } from '../../ui/button';

const MAX_SLIDES = 10;

/* ── Sortable Slide Thumbnail ── */
const SortableSlide = ({ slide, idx, isActive, onSelect, onDuplicate, onRemove, totalSlides }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slide.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}
      className="relative group flex-shrink-0" data-testid={`slide-thumb-${idx}`}>
      <div
        className={`w-20 h-20 rounded-lg cursor-pointer overflow-hidden transition-all border-2 ${
          isActive ? 'border-purple-500 shadow-lg shadow-purple-500/20' : 'border-border/30 hover:border-purple-400/50'
        }`}
        onClick={() => onSelect(idx)}
      >
        {slide.background_image ? (
          <img src={slide.background_image} alt={`Slide ${idx + 1}`}
            className="w-full h-full object-cover" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] text-white/50"
            style={{ backgroundColor: slide.background_color || '#1a1a2e' }}>
            {slide.layers?.length > 0 ? `${slide.layers.length} layers` : 'Empty'}
          </div>
        )}
        {/* Slide number */}
        <div className="absolute top-0.5 left-0.5 bg-black/60 text-white text-[8px] px-1 rounded">
          {idx + 1}
        </div>
      </div>

      {/* Drag handle */}
      <div
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10 bg-slate-700 rounded-full px-1 py-0.5"
        style={{ touchAction: 'none' }}
        {...attributes} {...listeners}
        data-testid={`slide-drag-${idx}`}
      >
        <GripVertical className="h-3 w-3 text-white rotate-90" />
      </div>

      {/* Hover actions */}
      <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-500 shadow"
          onClick={(e) => { e.stopPropagation(); onDuplicate(idx); }}
          title="Duplicate"
          data-testid={`slide-dup-${idx}`}>
          <Copy className="h-2.5 w-2.5" />
        </button>
        {totalSlides > 1 && (
          <button className="w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white hover:bg-red-500 shadow"
            onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
            title="Delete"
            data-testid={`slide-del-${idx}`}>
            <Trash2 className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export const SlideStrip = ({
  slides, activeIndex, onSelect, onAdd, onDuplicate, onRemove, onReorder,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );

  const slideIds = slides.map(s => s.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = slides.findIndex(s => s.id === active.id);
    const newIdx = slides.findIndex(s => s.id === over.id);
    if (oldIdx !== -1 && newIdx !== -1) {
      onReorder(oldIdx, newIdx);
    }
  };

  return (
    <div className="border-t border-border/50 bg-card" data-testid="slide-strip">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30">
        <span className="text-xs font-medium text-muted-foreground">Slides</span>
        <span className="text-[10px] text-muted-foreground/60">{slides.length}/{MAX_SLIDES}</span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1"
          disabled={slides.length >= MAX_SLIDES}
          onClick={onAdd} data-testid="add-slide-btn">
          <Plus className="h-3 w-3" /> Add Slide
        </Button>
      </div>

      <div className="flex items-center gap-3 px-3 py-3 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={slideIds} strategy={horizontalListSortingStrategy}>
            {slides.map((slide, idx) => (
              <SortableSlide
                key={slide.id}
                slide={slide}
                idx={idx}
                isActive={activeIndex === idx}
                onSelect={onSelect}
                onDuplicate={onDuplicate}
                onRemove={onRemove}
                totalSlides={slides.length}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add button at end */}
        {slides.length < MAX_SLIDES && (
          <div className="flex-shrink-0 w-20 h-20 rounded-lg border-2 border-dashed border-border/30 flex items-center justify-center cursor-pointer hover:border-purple-400 hover:text-purple-400 text-muted-foreground transition-colors"
            onClick={onAdd} data-testid="slide-strip-add">
            <Plus className="h-5 w-5" />
          </div>
        )}
      </div>
    </div>
  );
};
